import { NumberField } from "@/components/ui/number-field";
import { useToast } from "@/hooks/use-toast";
import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { AutoExpandingTextarea } from "@/components/auto-expanding-textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Calendar, Clock, Users, ListTodo, DollarSign, HelpCircle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { apiRequest } from "@/lib/queryClient";
import { type Category } from "@shared/schema";
import TrackerXMeasurementSection, {
  type NewTrackerFields,
} from "@/components/trackerx-measurement-section";

interface CreateGoalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  whyReason1: z.string().optional(),
  whyReason2: z.string().optional(),
  whyReason3: z.string().optional(),
  whyReason4: z.string().optional(),
  whyReason5: z.string().optional(),
  category: z.string().min(1, "Category is required"),
  priority: z.enum(["low", "medium", "high", "urgent"]).default("medium"),
  status: z.enum(["active", "completed", "paused", "future"]).default("active"),
  progress: z.number().int().min(0).max(100).default(0),
  progressCalculationType: z.enum(["manual", "milestones", "tasks", "habits", "custom", "trackerx"]).default("manual"),
  habitTargetCount: z.coerce.number<number>().optional(),
  customGoalType: z.enum(["number", "currency"]).optional(),
  customGoalTitle: z.string().optional(),
  customGoalTarget: z.coerce.number<number>().optional(),
  targetValue: z.string().optional(),
  currentValue: z.string().optional(),
  currentValueRaw: z.number().optional(),
  deadline: z.string().optional(),
  isFutureGoal: z.boolean().default(false),
  mediaGalleryExpanded: z.boolean().default(false),
  mediaSlideshowMode: z.string().default("static"),
  mediaSlideshowInterval: z.number().int().default(3),
  smartSpecific: z.boolean().default(false),
  smartMeasurable: z.boolean().default(false),
  smartAchievable: z.boolean().default(false),
  smartRelevant: z.boolean().default(false),
  smartTimeBound: z.boolean().default(false),
  smartBypassed: z.boolean().default(false),
  timeCost: z.coerce.number<number>().optional(),
  timeCostFrequency: z.enum(["daily", "weekly", "monthly"]).optional(),
  measuredTrackingMode: z.string().default("sum"),
  goalSource: z.string().optional(),
  healthMetric: z.string().optional(),
});

type FormInput = z.input<typeof formSchema>;
type FormData = z.output<typeof formSchema>;

interface MilestoneData {
  title: string;
  dueDate: string;
}

interface PlanningItemData {
  type: string;
  content: string;
}


export default function CreateGoalModal({ open, onOpenChange }: CreateGoalModalProps) {
  const [milestones, setMilestones] = useState<MilestoneData[]>([{ title: "", dueDate: "" }]);
  const [planningItems, setPlanningItems] = useState<PlanningItemData[]>([]);
  const [selectedTrackerIds, setSelectedTrackerIds] = useState<string[]>([]);
  const [newTrackers, setNewTrackers] = useState<NewTrackerFields[]>([]);
  const [trackerError, setTrackerError] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch categories from the database (include goals-only categories)
  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const form = useForm<FormInput, any, FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      whyReason1: "",
      whyReason2: "",
      whyReason3: "",
      whyReason4: "",
      whyReason5: "",
      category: "Personal",
      priority: "medium",
      status: "active",
      progress: 0,
      targetValue: "",
      currentValue: "",
      deadline: "",
      progressCalculationType: "manual",
      habitTargetCount: undefined,
      customGoalType: undefined,
      customGoalTitle: "",
      customGoalTarget: undefined,
      isFutureGoal: false,
      timeCost: undefined,
      timeCostFrequency: undefined,
    },
  });

  const progressType = form.watch("progressCalculationType");

  const createGoalMutation = useMutation({
    mutationFn: async (data: FormData) => {
      console.log('🎯 Create Goal Request:', data);
      try {
        const goalData = {
          ...data,
          deadline: data.deadline ? new Date(data.deadline) : null,
          whyReason1: data.whyReason1 || null,
          whyReason2: data.whyReason2 || null,
          whyReason3: data.whyReason3 || null,
          whyReason4: data.whyReason4 || null,
          whyReason5: data.whyReason5 || null,
          targetValue: data.targetValue || null,
          currentValue: data.currentValue || null,
          description: data.description || null,
          progressCalculationType: data.progressCalculationType || "manual",
          habitTargetCount: data.habitTargetCount || null,
          customGoalType: data.customGoalType || null,
          customGoalTitle: data.customGoalTitle || null,
          customGoalTarget: data.customGoalTarget || null,
          // Keep the legacy flag and canonical status in lockstep.
          status: data.status === "future" || data.isFutureGoal ? "future" : data.status,
          isFutureGoal: data.status === "future" || data.isFutureGoal,
          timeCost: data.timeCost || null,
          timeCostFrequency: data.timeCostFrequency || null,
        };
        
        console.log('📤 Sending goal data:', goalData);
        // Generate a key once per submission attempt; reuse it on network-
        // error retries so the server can deduplicate the request.
        if (!idempotencyKeyRef.current) {
          idempotencyKeyRef.current = crypto.randomUUID();
        }
        const response = await apiRequest("POST", "/api/goals", goalData, {
          "Idempotency-Key": idempotencyKeyRef.current,
        });
        console.log('📡 Create Goal Response:', { status: response.status, ok: response.ok });
        
        const newGoal = await response.json();
        console.log('✅ New goal created:', newGoal);
        
        // Create milestones for the goal
        const validMilestones = milestones.filter(m => m.title.trim() !== "");
        if (validMilestones.length > 0) {
          console.log('📝 Creating milestones:', validMilestones);
          await Promise.all(
            validMilestones.map((milestone) =>
              apiRequest("POST", `/api/goals/${newGoal.id}/milestones`, {
                title: milestone.title,
                dueDate: milestone.dueDate ? new Date(milestone.dueDate) : null,
              })
            )
          );
        }
        
        // Create planning items for the goal
        const validPlanningItems = planningItems.filter(p => p.content.trim() !== "");
        if (validPlanningItems.length > 0) {
          console.log('📝 Creating planning items:', validPlanningItems);
          await Promise.all(
            validPlanningItems.map((item) =>
              apiRequest("POST", `/api/goals/${newGoal.id}/planning-items`, {
                type: item.type,
                content: item.content,
              })
            )
          );
        }
        
        // Link and/or create the TrackerX records for trackerx measurement
        if (data.progressCalculationType === "trackerx") {
          const linkOps: Promise<unknown>[] = selectedTrackerIds.map((id) =>
            apiRequest("PATCH", `/api/trackerx/${id}`, { goalId: newGoal.id })
          );
          for (const newTracker of newTrackers) {
            linkOps.push(apiRequest("POST", "/api/trackerx", {
              name: newTracker.name.trim(),
              // Inherit the goal's category (e.g. "Health") so reports and
              // percentages group the tracker with the goal it measures.
              ...(data.category ? { category: data.category } : {}),
              valueType: newTracker.valueType,
              unitLabel: newTracker.unitLabel.trim() || null,
              period: newTracker.period,
              goalValue: newTracker.goalValue !== "" ? Number(newTracker.goalValue) : null,
              goalId: newGoal.id,
            }));
          }
          await Promise.all(linkOps);
          queryClient.invalidateQueries({ queryKey: ["/api/trackerx"] });
        }

        return newGoal;
      } catch (error: any) {
        console.error('❌ Create Goal Error:', {
          message: error?.message,
          name: error?.name,
          status: error?.status,
          stack: error?.stack,
          fullError: error
        });
        throw error;
      }
    },
    onSuccess: (goal) => {
      // Submission confirmed — discard the key so a future open starts fresh.
      idempotencyKeyRef.current = null;
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "milestones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/categories"] });
      queryClient.invalidateQueries({ queryKey: ["/api/activities"] });
      
      form.reset();
      setMilestones([{ title: "", dueDate: "" }]);
      setPlanningItems([]);
      setSelectedTrackerIds([]);
      setNewTrackers([]);
      setTrackerError("");
      onOpenChange(false);
    },
    onError: (error: any) => {
      console.error('❌ Create Goal Mutation Error:', error);
      toast({
        title: "Couldn't save the goal",
        description: error?.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Idempotency key: generated once per submission attempt and reused on
  // network-error retries so the server can deduplicate. Cleared after a
  // confirmed success or when the modal closes (so the next open starts fresh).
  const idempotencyKeyRef = useRef<string | null>(null);

  // Clear the idempotency key and per-attempt TrackerX state whenever the
  // modal closes, so an abandoned create attempt doesn't leak into the next.
  useEffect(() => {
    if (!open) {
      idempotencyKeyRef.current = null;
      setSelectedTrackerIds([]);
      setNewTrackers([]);
      setTrackerError("");
    }
  }, [open]);

  const submitLockRef = useRef(false);
  useEffect(() => {
    if (!createGoalMutation.isPending) submitLockRef.current = false;
  }, [createGoalMutation.isPending]);

  const onSubmit = (data: FormData) => {
    if (submitLockRef.current || createGoalMutation.isPending) return;
    submitLockRef.current = true;
    console.log('🎯 Form submitted with data:', data);
    if (data.progressCalculationType === "trackerx") {
      if (selectedTrackerIds.length === 0 && newTrackers.length === 0) {
        setTrackerError("Select at least one TrackerX record or create a new one.");
        submitLockRef.current = false;
        return;
      }
      if (newTrackers.some((t) => !t.name.trim())) {
        setTrackerError("Enter a name for each new TrackerX record.");
        submitLockRef.current = false;
        return;
      }
      if (newTrackers.some((t) => !(Number.isFinite(Number(t.goalValue)) && Number(t.goalValue) > 0))) {
        setTrackerError("Enter a target value greater than 0 for each new TrackerX record.");
        submitLockRef.current = false;
        return;
      }
    }
    setTrackerError("");
    createGoalMutation.mutate(data);
  };

  // Log form errors whenever they change for debugging
  const formErrors = form.formState.errors;
  if (Object.keys(formErrors).length > 0) {
    console.log('📋 Form validation errors:', formErrors);
  }

  const addMilestone = () => {
    setMilestones([...milestones, { title: "", dueDate: "" }]);
  };

  const removeMilestone = (index: number) => {
    if (milestones.length > 1) {
      setMilestones(milestones.filter((_, i) => i !== index));
    }
  };

  const updateMilestoneTitle = (index: number, title: string) => {
    const updated = [...milestones];
    updated[index] = { ...updated[index], title };
    setMilestones(updated);
  };

  const updateMilestoneDueDate = (index: number, dueDate: string) => {
    const updated = [...milestones];
    updated[index] = { ...updated[index], dueDate };
    setMilestones(updated);
  };

  // Planning item management functions
  const getPlanningItemsByType = (type: string) => {
    return planningItems.filter(item => item.type === type);
  };

  const addPlanningItem = (type: string) => {
    setPlanningItems([...planningItems, { type, content: "" }]);
  };

  const updatePlanningItem = (type: string, index: number, content: string) => {
    const itemsOfType = getPlanningItemsByType(type);
    const globalIndex = planningItems.findIndex(
      (item, i) => item.type === type && planningItems.slice(0, i + 1).filter(p => p.type === type).length === index + 1
    );
    if (globalIndex !== -1) {
      const updated = [...planningItems];
      updated[globalIndex] = { ...updated[globalIndex], content };
      setPlanningItems(updated);
    }
  };

  const removePlanningItem = (type: string, index: number) => {
    let count = 0;
    const updated = planningItems.filter((item) => {
      if (item.type === type) {
        if (count === index) {
          count++;
          return false;
        }
        count++;
      }
      return true;
    });
    setPlanningItems(updated);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="modal-create-goal">
        <DialogHeader>
          <DialogTitle>Create New Goal</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Display form errors if any */}
            {Object.keys(form.formState.errors).length > 0 && (
              <div className="bg-destructive/15 text-destructive p-3 rounded-lg text-sm" data-testid="form-errors">
                <p className="font-medium">Please fix the following errors:</p>
                <ul className="list-disc list-inside mt-1">
                  {Object.entries(form.formState.errors).map(([field, error]) => (
                    <li key={field}>{field}: {(error as any)?.message || 'Invalid value'}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Goal Title */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Goal Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="What do you want to achieve?"
                      {...field}
                      data-testid="input-goal-title"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <AutoExpandingTextarea
                      placeholder="Describe your goal in detail..."
                      minRows={3}
                      maxRows={15}
                      {...field}
                      value={field.value || ""}
                      data-testid="textarea-goal-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Why Section */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">Why</h3>
                <p className="text-sm text-muted-foreground">
                  Explain how this will help you, someone else or improve your life
                </p>
              </div>
              
              <FormField
                control={form.control}
                name="whyReason1"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Reason 1"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-why-reason-1"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whyReason2"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Reason 2"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-why-reason-2"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whyReason3"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Reason 3"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-why-reason-3"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whyReason4"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Reason 4"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-why-reason-4"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="whyReason5"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder="Reason 5"
                        {...field}
                        value={field.value || ""}
                        data-testid="input-why-reason-5"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Progress Tracking */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">Progress Tracking</h3>
                <p className="text-sm text-muted-foreground">
                  Choose how you want to track progress for this goal
                </p>
              </div>

              <FormField
                control={form.control}
                name="progressCalculationType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Progress Calculation Method</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-progress-type">
                          <SelectValue placeholder="Select calculation method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="manual">Manual - Set progress percentage manually</SelectItem>
                        <SelectItem value="milestones">Milestone-Based - Calculated from milestone completion</SelectItem>
                        <SelectItem value="tasks">Sub-Task-Based - Calculated from sub-task completion</SelectItem>
                        <SelectItem value="habits">Habit Count - Track habit click completions</SelectItem>
                        <SelectItem value="custom">Custom Measured - Track specific numbers or amounts</SelectItem>
                        <SelectItem value="trackerx">TrackerX - Measure with a TrackerX record (e.g., calories, carbs)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* TrackerX record picker (shown only for trackerx progress type) */}
              {progressType === "trackerx" && (
                <>
                  <TrackerXMeasurementSection
                    enabled={progressType === "trackerx"}
                    selectedTrackerIds={selectedTrackerIds}
                    onToggleTracker={(id) => {
                      setSelectedTrackerIds((prev) =>
                        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                      );
                      setTrackerError("");
                    }}
                    newTrackers={newTrackers}
                    onNewTrackersChange={(list) => { setNewTrackers(list); setTrackerError(""); }}
                  />
                  {trackerError && (
                    <p className="text-sm text-destructive" data-testid="text-tracker-error">{trackerError}</p>
                  )}
                </>
              )}

              {/* Habit Target Count (shown only for habit progress type) */}
              {progressType === "habits" && (
                <FormField
                  control={form.control}
                  name="habitTargetCount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target Habit Count</FormLabel>
                      <FormControl>
                        <NumberField
                          min={1}
                          placeholder="e.g., 100 for 100 habit clicks = 100%"
                          value={field.value || undefined}
                          onValueChange={field.onChange}
                          name={field.name}
                          onBlur={field.onBlur}
                          ref={field.ref}
                          data-testid="input-habit-target-count"
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        How many habit completions equal 100% progress?
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Custom Goal Fields (shown only for custom progress type) */}
              {progressType === "custom" && (
                <>
                  <FormField
                    control={form.control}
                    name="customGoalType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Measurement Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-custom-goal-type">
                              <SelectValue placeholder="Select measurement type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="number">Number (e.g., pounds, miles, calls)</SelectItem>
                            <SelectItem value="currency">Currency (e.g., dollars, revenue)</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="customGoalTitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>What are you measuring?</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., Pounds Lost, Sales Calls Made, Revenue Generated"
                            {...field}
                            value={field.value || ""}
                            data-testid="input-custom-goal-title"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="customGoalTarget"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Target Amount</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="e.g., 20 for lose 20 pounds"
                            {...field}
                            onChange={(e) => field.onChange(parseFloat(e.target.value) || undefined)}
                            value={field.value || ""}
                            data-testid="input-custom-goal-target"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </>
              )}

              {/* Info messages for auto-calculated progress types */}
              {progressType === "milestones" && (
                <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                  ℹ️ Progress will be automatically calculated based on milestone completion percentage
                </div>
              )}
              {progressType === "tasks" && (
                <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                  ℹ️ Progress will be automatically calculated based on task completion percentage
                </div>
              )}
            </div>

            {/* Category & Priority */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-goal-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.filter(c => c.scope === "goals").length === 0 ? (
                          <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                        ) : (
                          categories.filter(c => c.scope === "goals" && c.name).map((category) => (
                            <SelectItem key={category.id} value={category.name!}>
                              <span className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: category.color }} />
                                {category.name}
                              </span>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-goal-priority">
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="high">High Priority</SelectItem>
                        <SelectItem value="medium">Medium Priority</SelectItem>
                        <SelectItem value="low">Low Priority</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select onValueChange={(value) => {
                      field.onChange(value);
                      form.setValue("isFutureGoal", value === "future");
                    }} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-goal-status">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="future">Future Goal</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Future Goal Toggle */}
            <FormField
              control={form.control}
              name="isFutureGoal"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Future Goal
                    </FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Mark this as a goal you plan to work on later
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        form.setValue("status", checked ? "future" : "active");
                      }}
                      data-testid="switch-future-goal"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Deadline & Target */}
            <div className={`grid gap-4 ${progressType === "custom" ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
              <FormField
                control={form.control}
                name="deadline"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Target Deadline</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          data-testid="input-goal-deadline"
                        />
                      </FormControl>
                      {field.value && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => field.onChange("")}
                          data-testid="button-clear-deadline"
                          title="Clear deadline"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {progressType !== "custom" && (
                <FormField
                  control={form.control}
                  name="targetValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Target Value (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g., 10000, 24 books, 21km"
                          {...field}
                          value={field.value || ""}
                          data-testid="input-goal-target-value"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* Time Cost Section */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FormLabel className="mb-0">Time Cost</FormLabel>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>The estimated time commitment needed to accomplish this goal. Enter the hours you'll dedicate per day, week, or month.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-xs text-muted-foreground">Enter hours as decimals (e.g., 0.5 = 30 min, 1.5 = 1 hr 30 min)</p>
              <div className="grid grid-cols-3 gap-2 items-end">
                <FormField
                  control={form.control}
                  name="timeCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          step="0.1"
                          placeholder="e.g. 0.5"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                          data-testid="input-goal-time-cost"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="timeCostFrequency"
                  render={({ field }) => (
                    <FormItem>
                      <Select onValueChange={field.onChange} value={field.value || ""}>
                        <FormControl>
                          <SelectTrigger data-testid="select-time-cost-frequency">
                            <SelectValue placeholder="Frequency" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="text-sm text-muted-foreground pb-2">
                  {(() => {
                    const timeCost = form.watch("timeCost");
                    const frequency = form.watch("timeCostFrequency");
                    const deadline = form.watch("deadline");
                    
                    if (!timeCost || !frequency || !deadline) return null;
                    
                    const deadlineDate = new Date(deadline);
                    const today = new Date();
                    const diffTime = deadlineDate.getTime() - today.getTime();
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    
                    if (diffDays <= 0) return <span className="text-destructive">Past deadline</span>;
                    
                    let totalHours = 0;
                    if (frequency === "daily") {
                      totalHours = timeCost * diffDays;
                    } else if (frequency === "weekly") {
                      totalHours = timeCost * Math.ceil(diffDays / 7);
                    } else if (frequency === "monthly") {
                      totalHours = timeCost * Math.ceil(diffDays / 30);
                    }
                    
                    return <span className="font-medium">= {totalHours} hrs total</span>;
                  })()}
                </div>
              </div>
            </div>

            {/* Milestones */}
            <div>
              <FormLabel>Milestones</FormLabel>
              <div className="space-y-3 mb-3">
                {milestones.map((milestone, index) => (
                  <div key={index} className="space-y-2 p-3 border rounded-md bg-muted/50">
                    <div className="flex items-center space-x-2">
                      <Input
                        placeholder={`Milestone ${index + 1} title`}
                        value={milestone.title}
                        onChange={(e) => updateMilestoneTitle(index, e.target.value)}
                        className="flex-1"
                        data-testid={`input-milestone-title-${index}`}
                      />
                      {milestones.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeMilestone(index)}
                          data-testid={`button-remove-milestone-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <Input
                        type="date"
                        placeholder="Due date (optional)"
                        value={milestone.dueDate}
                        onChange={(e) => updateMilestoneDueDate(index, e.target.value)}
                        className="flex-1"
                        data-testid={`input-milestone-duedate-${index}`}
                      />
                      {milestone.dueDate && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => updateMilestoneDueDate(index, "")}
                          data-testid={`button-clear-milestone-duedate-${index}`}
                          title="Clear due date"
                        >
                          ×
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addMilestone}
                className="flex items-center space-x-1"
                data-testid="button-add-milestone"
              >
                <Plus className="w-4 h-4" />
                <span>Add Milestone</span>
              </Button>
            </div>

            {/* Goal Planning Sections */}
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">Goal Planning</h3>
                <p className="text-sm text-muted-foreground">
                  Plan the details of how you'll achieve this goal
                </p>
              </div>

              {/* What Do I Need To Know? */}
              <div className="border rounded-md p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ListTodo className="w-4 h-4 text-primary" />
                  <h4 className="font-medium">What Do I Need To Know?</h4>
                </div>
                <div className="space-y-2">
                  {getPlanningItemsByType("what_know").map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        placeholder="Enter knowledge or skill..."
                        value={item.content}
                        onChange={(e) => updatePlanningItem("what_know", index, e.target.value)}
                        className="flex-1"
                        data-testid={`input-what-know-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removePlanningItem("what_know", index)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`button-remove-what-know-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addPlanningItem("what_know")}
                  className="mt-2"
                  data-testid="button-add-what-know"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Another Record
                </Button>
              </div>

              {/* Who Do I Need To Know? */}
              <div className="border rounded-md p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-primary" />
                  <h4 className="font-medium">Who Do I Need To Know?</h4>
                </div>
                <div className="space-y-2">
                  {getPlanningItemsByType("who_need_know").map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        placeholder="Enter person or contact..."
                        value={item.content}
                        onChange={(e) => updatePlanningItem("who_need_know", index, e.target.value)}
                        className="flex-1"
                        data-testid={`input-who-need-know-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removePlanningItem("who_need_know", index)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`button-remove-who-need-know-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addPlanningItem("who_need_know")}
                  className="mt-2"
                  data-testid="button-add-who-need-know"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Another Record
                </Button>
              </div>

              {/* What Do I Have to Do? */}
              <div className="border rounded-md p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ListTodo className="w-4 h-4 text-primary" />
                  <h4 className="font-medium">What Do I Have to Do?</h4>
                </div>
                <div className="space-y-2">
                  {getPlanningItemsByType("what_do").map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        placeholder="Enter action item..."
                        value={item.content}
                        onChange={(e) => updatePlanningItem("what_do", index, e.target.value)}
                        className="flex-1"
                        data-testid={`input-what-do-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removePlanningItem("what_do", index)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`button-remove-what-do-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addPlanningItem("what_do")}
                  className="mt-2"
                  data-testid="button-add-what-do"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Another Record
                </Button>
              </div>

              {/* Resources and Budget */}
              <div className="border rounded-md p-4">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-primary" />
                  <h4 className="font-medium">What are the Resources and Budget I need?</h4>
                </div>
                <div className="space-y-2">
                  {getPlanningItemsByType("resources_budget").map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        placeholder="Enter resource or budget item..."
                        value={item.content}
                        onChange={(e) => updatePlanningItem("resources_budget", index, e.target.value)}
                        className="flex-1"
                        data-testid={`input-resources-budget-${index}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removePlanningItem("resources_budget", index)}
                        className="text-muted-foreground hover:text-destructive"
                        data-testid={`button-remove-resources-budget-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addPlanningItem("resources_budget")}
                  className="mt-2"
                  data-testid="button-add-resources-budget"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Another Record
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="button-cancel-goal"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitLockRef.current || createGoalMutation.isPending}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="button-submit-goal"
              >
                {createGoalMutation.isPending ? "Creating..." : "Create Goal"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
